import React from 'react';
import { Box, Typography, Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import GroupWorkIcon from '@material-ui/icons/GroupWork';
import BusinessIcon from '@material-ui/icons/Business';
import TuneIcon from '@material-ui/icons/Tune';
import PeopleIcon from '@material-ui/icons/People';
import RoomIcon from '@material-ui/icons/Room';
import ExtensionIcon from '@material-ui/icons/Extension';

// Ecrã dedicado — explica o modelo de peças em grande e de forma didática.
// Aberto a partir do cartão "Como funciona o modelo" no dashboard.

const PIECES = [
  {
    icon: GroupWorkIcon, name: 'Grupo', tone: 'slate',
    one: 'Uma holding que agrega várias Organizações (opcional).',
    detail: 'Serve só para agrupar empresas do mesmo dono. Se não precisares, ignora — uma Organização pode viver sem Grupo.',
  },
  {
    icon: BusinessIcon, name: 'Organização', tone: 'green',
    one: 'A empresa. É a dona dos dados (rotas, OTs, veículos, condutores).',
    detail: 'Tudo o que se cria no planner pertence a uma Organização: rotas, OTs, veículos, condutores e favoritos. A configuração dela vive no seu Perfil.',
  },
  {
    icon: TuneIcon, name: 'Perfil', tone: 'indigo',
    one: 'A configuração da Organização. Cada Organização tem o seu.',
    detail: 'Define os ecrãs (variante de UI), as preferências (moeda, país, rotas, veículo), o fluxo das OT e que Módulos estão ligados. É a MESMA coisa que as "Preferências" dentro do planner — mexes num, muda no outro.',
  },
  {
    icon: ExtensionIcon, name: 'Módulos', tone: 'orange',
    one: 'As peças funcionais que se ligam/desligam num Perfil.',
    detail: 'Cada Módulo é uma capacidade do planner (planeador de rotas, tacógrafo, combustível, portagens, relatórios…). O Perfil escolhe quais estão ativos; assim controlas o que cada cliente vê.',
  },
  {
    icon: PeopleIcon, name: 'Utilizador', tone: 'teal',
    one: 'A pessoa que entra. Pertence a uma Organização.',
    detail: 'Herda o Perfil da Organização e traz o seu papel, o nível de permissão (viewer / operator / manager) e, opcionalmente, uma Zona e um Perfil add-on (só para dar módulos extra).',
  },
  {
    icon: RoomIcon, name: 'Zona', tone: 'violet',
    one: 'Uma sub-divisão da Organização para limitar a visibilidade.',
    detail: 'Um Utilizador de âmbito "zona" só vê os dados da sua Zona; de âmbito "organização" vê tudo. Útil para separar operações por região ou delegação.',
  },
];

const TONES = {
  slate: { bg: '#eef0f3', fg: '#5b6472', bd: '#e3e6ea' },
  green: { bg: '#e9f7ec', fg: '#2e7d32', bd: '#cdeed4' },
  indigo: { bg: '#eeeefc', fg: '#4338ca', bd: '#dcdcfa' },
  teal: { bg: '#e6f7f6', fg: '#0d7a72', bd: '#c8eeeb' },
  orange: { bg: '#fff1e3', fg: '#c2410c', bd: '#fdd9bd' },
  violet: { bg: '#f3effd', fg: '#7c3aed', bd: '#e5dcfb' },
};

const useStyles = makeStyles(() => ({
  '@global': { '@keyframes upIn': { '0%': { opacity: 0, transform: 'translateY(16px)' }, '100%': { opacity: 1, transform: 'translateY(0)' } } },
  backBtn: { textTransform: 'none', color: 'var(--adm-brand, #2f5d50)', fontWeight: 700, marginBottom: 14, '&:hover': { background: 'var(--adm-brand-soft, rgba(47,93,80,0.07))' } },
  header: { marginBottom: 24 },
  title: { fontSize: '1.9rem', fontWeight: 800, color: 'var(--adm-text, #344767)', letterSpacing: '-0.02em' },
  subtitle: { fontSize: '0.9rem', color: 'var(--adm-text-dim, #67748e)', marginTop: 4, maxWidth: 720, lineHeight: 1.5 },

  // Diagrama grande
  card: { background: 'var(--adm-card, #fff)', borderRadius: 18, boxShadow: '0 4px 18px rgba(52,71,103,0.08)', padding: '30px 32px', marginBottom: 26, animation: '$upIn 0.4s ease-out both' },
  cardTitle: { fontSize: '1.15rem', fontWeight: 800, color: 'var(--adm-text, #344767)', marginBottom: 22 },
  flowRow: { display: 'flex', alignItems: 'stretch', gap: 0, flexWrap: 'wrap' },
  node: { borderRadius: 16, padding: '18px 20px', minWidth: 180, position: 'relative', border: '1px solid', boxShadow: '0 2px 8px rgba(52,71,103,0.05)' },
  nodeName: { fontSize: '1.08rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8 },
  nodeSub: { fontSize: '0.74rem', fontWeight: 600, color: 'var(--adm-text-muted, #8a94a6)', marginTop: 3 },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 10 },
  chip: { fontSize: '0.66rem', fontWeight: 700, background: 'rgba(52,71,103,0.05)', color: 'var(--adm-text-dim, #67748e)', padding: '3px 9px', borderRadius: 6 },
  conn: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: 130, padding: '0 8px' },
  connLabel: { fontSize: '0.66rem', fontWeight: 700, color: 'var(--adm-brand-mid, #5a8577)', textAlign: 'center', marginBottom: 4, lineHeight: 1.3 },
  vconn: { display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '6px 0 6px 84px' },
  vLabel: { fontSize: '0.66rem', fontWeight: 700, color: 'var(--adm-brand-mid, #5a8577)', marginBottom: 2 },

  // Peças (grelha)
  piecesGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(330px,1fr))', gap: 18, marginBottom: 26 },
  piece: { background: 'var(--adm-card, #fff)', borderRadius: 16, boxShadow: '0 4px 18px rgba(52,71,103,0.08)', padding: '20px 22px', animation: '$upIn 0.4s ease-out both' },
  pieceTop: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 },
  pieceIcon: { width: 46, height: 46, borderRadius: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  pieceName: { fontSize: '1.1rem', fontWeight: 800, color: 'var(--adm-text, #344767)' },
  pieceOne: { fontSize: '0.82rem', fontWeight: 600, color: 'var(--adm-text-dim, #475569)', marginBottom: 8, lineHeight: 1.45 },
  pieceDetail: { fontSize: '0.8rem', color: 'var(--adm-text-dim, #67748e)', lineHeight: 1.55 },

  note: { background: 'var(--adm-brand-soft, #eef5f1)', border: '1px solid #d8e9e1', borderRadius: 14, padding: '18px 22px', fontSize: '0.86rem', color: '#3f6b5c', fontWeight: 500, lineHeight: 1.6 },
}));

function Arrow({ label, classes }) {
  return (
    <Box className={classes.conn}>
      {label && <Typography className={classes.connLabel}>{label}</Typography>}
      <Box style={{ display: 'flex', alignItems: 'center', color: 'var(--adm-text-muted, #cbd5e1)' }}>
        <Box style={{ height: 2, width: 48, background: 'var(--adm-text-muted, #cbd5e1)' }} />
        <ArrowForwardIcon style={{ fontSize: 20 }} />
      </Box>
    </Box>
  );
}

function AdminModel({ onBack }) {
  const classes = useStyles();

  return (
    <Box>
      <Button className={classes.backBtn} startIcon={<ArrowBackIcon />} onClick={onBack}>
        Voltar ao dashboard
      </Button>

      <Box className={classes.header}>
        <Typography className={classes.title}>Como funciona o modelo EcoPlanner</Typography>
        <Typography className={classes.subtitle}>
          O backoffice é construído por peças que se encaixam. Percebendo estas seis peças e o sentido das ligações,
          consegues configurar qualquer cliente sem duplicar trabalho.
        </Typography>
      </Box>

      {/* Diagrama grande */}
      <Box className={classes.card}>
        <Typography className={classes.cardTitle}>O fluxo, das peças aos dados</Typography>

        <Box className={classes.flowRow}>
          <Box className={classes.node} style={{ background: '#eef0f3', color: '#5b6472', borderColor: '#e3e6ea' }}>
            <Typography className={classes.nodeName}><GroupWorkIcon style={{ fontSize: 19 }} /> Grupo</Typography>
            <Typography className={classes.nodeSub}>holding (opcional)</Typography>
          </Box>
          <Arrow label="agrega" classes={classes} />
          <Box className={classes.node} style={{ background: '#e9f7ec', color: '#2e7d32', borderColor: '#cdeed4' }}>
            <Typography className={classes.nodeName}><BusinessIcon style={{ fontSize: 19 }} /> Organização</Typography>
            <Typography className={classes.nodeSub}>a empresa · dona dos dados</Typography>
            <Box className={classes.chips}><span className={classes.chip}>rotas</span><span className={classes.chip}>OTs</span><span className={classes.chip}>veículos</span></Box>
          </Box>
          <Arrow label="tem o seu" classes={classes} />
          <Box className={classes.node} style={{ background: '#eeeefc', color: '#4338ca', borderColor: '#dcdcfa' }}>
            <Typography className={classes.nodeName}><TuneIcon style={{ fontSize: 19 }} /> Perfil</Typography>
            <Typography className={classes.nodeSub}>a "receita" de configuração</Typography>
            <Box className={classes.chips}><span className={classes.chip}>preferências</span><span className={classes.chip}>módulos</span><span className={classes.chip}>fluxo OT</span><span className={classes.chip}>variante UI</span></Box>
          </Box>
        </Box>

        <Box className={classes.vconn}>
          <Typography className={classes.vLabel}>tem</Typography>
          <ArrowDownwardIcon style={{ color: 'var(--adm-text-muted, #cbd5e1)', fontSize: 22 }} />
        </Box>

        <Box className={classes.flowRow}>
          <Box className={classes.node} style={{ background: '#e6f7f6', color: '#0d7a72', borderColor: '#c8eeeb' }}>
            <Typography className={classes.nodeName}><PeopleIcon style={{ fontSize: 19 }} /> Utilizador</Typography>
            <Typography className={classes.nodeSub}>a pessoa que entra</Typography>
            <Box className={classes.chips}><span className={classes.chip}>papel</span><span className={classes.chip}>permissão</span><span className={classes.chip}>zona</span><span className={classes.chip}>add-on</span></Box>
          </Box>
          <Arrow label="pertence a" classes={classes} />
          <Box className={classes.node} style={{ background: '#e9f7ec', color: '#2e7d32', borderColor: '#cdeed4', display: 'flex', alignItems: 'center' }}>
            <Typography className={classes.nodeSub} style={{ color: '#2e7d32', fontSize: '0.78rem' }}>...uma Organização →<br />usa o Perfil dela</Typography>
          </Box>
        </Box>
      </Box>

      {/* Peças em detalhe */}
      <Box className={classes.piecesGrid}>
        {PIECES.map((p, i) => {
          const t = TONES[p.tone];
          return (
            <Box key={p.name} className={classes.piece} style={{ animationDelay: `${i * 0.05}s` }}>
              <Box className={classes.pieceTop}>
                <Box className={classes.pieceIcon} style={{ background: t.bg, border: `1px solid ${t.bd}` }}>
                  <p.icon style={{ color: t.fg, fontSize: 24 }} />
                </Box>
                <Typography className={classes.pieceName}>{p.name}</Typography>
              </Box>
              <Typography className={classes.pieceOne}>{p.one}</Typography>
              <Typography className={classes.pieceDetail}>{p.detail}</Typography>
            </Box>
          );
        })}
      </Box>

      <Box className={classes.note}>
        Regra de ouro: a Organização é dona dos dados; a configuração dela vive no seu Perfil. Cada Organização tem um
        Perfil próprio, e esse Perfil é exatamente o que as "Preferências" dentro do planner alteram — os dois estão
        sempre em sincronia. Para dar uma peça extra só a uma pessoa, usa um Perfil add-on no Utilizador, sem tocar no
        Perfil da Organização.
      </Box>
    </Box>
  );
}

export default AdminModel;
